import type { Session } from '@supabase/supabase-js';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { queryClient } from '@/lib/query-client';
import { supabase } from '@/lib/supabase';

interface SessionState {
  session: Session | null;
}

const SessionContext = createContext<SessionState>({ session: null });

/** Mirrors the Supabase session into React. Signing out clears all cached server data. */
export function SessionProvider({
  initial,
  onChange,
  children,
}: {
  initial: Session | null;
  onChange: (session: Session | null) => void;
  children: ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(initial);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      if (event === 'SIGNED_OUT') queryClient.clear();
      onChange(next);
    });
    return () => data.subscription.unsubscribe();
  }, [onChange]);

  return <SessionContext.Provider value={{ session }}>{children}</SessionContext.Provider>;
}

export const useSession = () => useContext(SessionContext);
